import * as THREE from 'three'
import { MapControls } from 'three/examples/jsm/controls/MapControls.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import Experience from '../Experience.js'

export default class Camera {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.sizes = this.experience.sizes
        this.canvas = this.experience.canvas
        this.debug = this.experience.debug
        this.config = this.experience.config.camera

        this.controlMode = this.config.controlMode ?? 'scroll'
        this.fovManual = false

        this.setInstance()
        this.setOrthographic()
        this.setControls()
        this.setMapControls()
        this.setDebug()
    }

    // Caméra active : perspective pour la visite et l'exploration,
    // orthographique pour le plan du parcours.
    get instance() {
        return this.controlMode === 'map' ? this.orthographic : this.perspective
    }

    /* ---------------- CAMERA ---------------- */

    getEffectiveFov(fov = this.config.fov) {
        const offset = this.experience.isMobile ? (this.config.fovMobileOffset ?? 0) : 0
        return fov + offset
    }

    setInstance() {
        this.perspective = new THREE.PerspectiveCamera(
            this.getEffectiveFov(),
            this.sizes.width / this.sizes.height,
            this.config.near,
            this.config.far
        )

        this.perspective.position.set(
            this.config.position.x,
            this.config.position.y,
            this.config.position.z
        )

        this.scene.add(this.perspective)
    }

    setOrthographic() {
        this.orthographic = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 80)
        this.mapPoints = []
        this.mapConfig = this.config.map ?? {}
        this.scene.add(this.orthographic)
    }

    setControls() {
        // Exploration libre : orbite autour du point visé (glisser pour
        // pivoter, clic droit pour se déplacer, molette pour zoomer)
        this.controls = new OrbitControls(this.perspective, this.canvas)
        this.controls.target.set(
            this.config.timeline.target.x,
            this.config.timeline.target.y,
            this.config.timeline.target.z
        )
        const free = this.config.freeControls ?? {}
        this.controls.enableDamping = true
        this.baseDampingFactor = free.dampingFactor ?? 0.05
        this.controls.dampingFactor = this.baseDampingFactor
        this.controls.panSpeed = free.panSpeed ?? 1
        this.controls.rotateSpeed = free.rotateSpeed ?? 1
        this.controls.zoomSpeed = free.zoomSpeed ?? 1
        this.controls.enablePan = true
        this.controls.enableRotate = true
        this.controls.enableZoom = true

        // Exploration libre : on reste au-dessus du sol et dans l'emprise
        // du terrain (plan 10 x 10 centré sur l'origine).
        this.controls.maxPolarAngle = Math.PI * 0.46
        this.controls.minDistance = 0.08
        this.controls.maxDistance = 5
        this.controls.screenSpacePanning = false
        this.targetBounds = new THREE.Box3(
            new THREE.Vector3(-4.5, -1, -4.5),
            new THREE.Vector3(4.5, 1, 4.5)
        )
        this.clampedTarget = new THREE.Vector3()
        this.groundFollow = free.groundFollow ?? 4
        this.groundClearance = free.groundClearance ?? 0.06

        this.applyControlMode()
    }

    // Plan du parcours : zoom, déplacement et inclinaison (clic droit)
    // jusqu'à π/3 de la verticale. L'orientation reste fixe pour garder
    // une lecture cartographique stable.
    setMapControls() {
        this.mapControls = new MapControls(this.orthographic, this.canvas)
        this.mapControls.enableRotate = true
        this.mapControls.rotateSpeed = 0.5
        this.mapControls.minPolarAngle = 0
        this.mapControls.maxPolarAngle = this.mapConfig.maxTilt ?? Math.PI / 3
        this.mapControls.enableDamping = true
        this.mapControls.dampingFactor = this.baseDampingFactor
        this.mapControls.panSpeed = 0.8
        this.mapControls.zoomSpeed = 0.6
        this.mapControls.minZoom = 0.8
        this.mapControls.maxZoom = 4
        this.mapControls.enabled = false
        this.mapBounds = new THREE.Box3()
    }

    // Points à garder dans le cadre du plan (trous et départs)
    setMapPoints(points) {
        this.mapPoints = points
        this.mapBounds.setFromPoints(points)
    }

    // Cadre orthographique incliné englobant tout le parcours. On teste
    // plusieurs orientations et on garde celle qui remplit le mieux
    // l'écran (le grand axe du parcours suit celui de l'écran).
    updateMapFrame() {
        if (!this.mapPoints.length) return

        const camera = this.orthographic
        const aspect = this.sizes.width / this.sizes.height
        const center = this.mapBounds.getCenter(new THREE.Vector3())
        const elevation = THREE.MathUtils.degToRad(this.mapConfig.elevation ?? 58)
        const padding = this.mapConfig.padding ?? 1.22

        let best = null
        for (let deg = 0; deg < 360; deg += 15) {
            const frame = this.computeMapFrame(center, elevation, THREE.MathUtils.degToRad(deg), aspect, padding)
            if (!best || frame.halfH < best.halfH - 1e-4) best = { ...frame, azimuth: THREE.MathUtils.degToRad(deg) }
        }

        // Azimut verrouillé : le clic droit n'agit que sur l'inclinaison
        const azimuth = Math.atan2(Math.sin(best.azimuth), Math.cos(best.azimuth))
        this.mapControls.minAzimuthAngle = azimuth
        this.mapControls.maxAzimuthAngle = azimuth

        camera.position.copy(best.position)
        camera.up.set(0, 1, 0)
        camera.lookAt(center)
        camera.left = best.cx - best.halfW
        camera.right = best.cx + best.halfW
        camera.top = best.cy + best.halfH
        camera.bottom = best.cy - best.halfH
        camera.zoom = 1
        camera.updateProjectionMatrix()

        this.mapControls.target.copy(center)
        this.mapControls.update()
    }

    computeMapFrame(center, elevation, azimuth, aspect, padding) {
        const camera = this.orthographic
        const position = new THREE.Vector3(
            Math.sin(azimuth) * Math.cos(elevation),
            Math.sin(elevation),
            Math.cos(azimuth) * Math.cos(elevation)
        ).multiplyScalar(20).add(center)

        camera.position.copy(position)
        camera.up.set(0, 1, 0)
        camera.lookAt(center)
        camera.updateMatrixWorld()

        const viewPoint = new THREE.Vector3()
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
        for (const point of this.mapPoints) {
            viewPoint.copy(point).applyMatrix4(camera.matrixWorldInverse)
            minX = Math.min(minX, viewPoint.x)
            maxX = Math.max(maxX, viewPoint.x)
            minY = Math.min(minY, viewPoint.y)
            maxY = Math.max(maxY, viewPoint.y)
        }

        let halfW = ((maxX - minX) / 2) * padding
        let halfH = ((maxY - minY) / 2) * padding
        if (halfW / halfH < aspect) halfW = halfH * aspect
        else halfH = halfW / aspect

        return { position, halfW, halfH, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 }
    }

    setOrbitTarget(target) {
        this.controls.target.copy(target)
    }

    // Hauteur visible du plan en unités monde (pour dimensionner les repères)
    getMapViewHeight() {
        const camera = this.orthographic
        return (camera.top - camera.bottom) / camera.zoom
    }

    // Garde l'exploration dans le terrain : le pivot épouse le relief en
    // douceur (amorti indépendant du framerate) et la caméra ne passe
    // jamais sous les collines.
    keepOrbitInBounds() {
        const target = this.controls.target
        const terrain = this.experience.world?.terrain
        const dt = THREE.MathUtils.clamp((this.experience.time.delta ?? 16) / 1000, 1 / 240, 0.1)
        const follow = 1 - Math.exp(-this.groundFollow * dt)

        this.clampedTarget.copy(target).clamp(this.targetBounds.min, this.targetBounds.max)
        if (terrain) {
            const ground = terrain.getSmoothHeightAt(this.clampedTarget.x, this.clampedTarget.z)
            this.clampedTarget.y += (ground - this.clampedTarget.y) * follow
        }

        if (!this.clampedTarget.equals(target)) {
            // Décale caméra et cible ensemble pour ne pas faire pivoter la vue.
            this.instance.position.add(this.clampedTarget.sub(target))
            target.add(this.clampedTarget)
        }

        if (!terrain) return

        const position = this.instance.position
        const minY = terrain.getSmoothHeightAt(position.x, position.z) + this.groundClearance
        if (position.y < minY) position.y += (minY - position.y) * Math.max(follow, 0.5)
    }

    setControlMode(mode) {
        this.controlMode = mode
        this.applyControlMode()
    }

    setFov(fov) {
        this.fovManual = true
        this.perspective.fov = fov
        this.perspective.updateProjectionMatrix()
    }

    applyControlMode() {
        if (!this.controls) return

        const isOrbit = this.controlMode === 'orbit'
        const isMap = this.controlMode === 'map'
        this.controls.enabled = isOrbit
        if (this.mapControls) this.mapControls.enabled = isMap
        if (isMap) this.updateMapFrame()

        document.body.classList.toggle('map-mode', isMap)
        this.experience.ui?.setScrollUiActive?.(this.controlMode === 'scroll')
    }

    setDebug() {
        if (!this.debug.active) return

        this.debugFolder = this.debug.ui.addFolder({ title: 'Camera', expanded: true })
        this.debugParams = {
            controlMode: this.controlMode,
            fov: this.config.fov
        }

        this.debugFolder
            .addBinding(this.debugParams, 'fov', { min: 1, max: 120, step: 1 })
            .on('change', ev => this.setFov(ev.value))

        this.debugFolder
            .addBinding(this.debugParams, 'controlMode', {
                label: 'Mode',
                options: {
                    'Animation scroll': 'scroll',
                    OrbitControls: 'orbit'
                }
            })
            .on('change', ev => this.setControlMode(ev.value))
    }

    update() {
        if (this.controlMode !== 'orbit' && this.controlMode !== 'map') return

        // Le damping des contrôles s'applique par frame : on le ramène à
        // une base 60 fps pour garder la même glisse à 60, 120 ou 144 Hz.
        const frames = THREE.MathUtils.clamp((this.experience.time.delta ?? 16) / (1000 / 60), 0.25, 6)
        const damping = 1 - Math.pow(1 - this.baseDampingFactor, frames)

        if (this.controlMode === 'map') {
            this.mapControls.dampingFactor = damping
            this.mapControls.update()
            this.keepMapInBounds()
            return
        }

        this.controls.dampingFactor = damping
        this.controls.update()
        this.keepOrbitInBounds()
    }

    // Le plan ne peut pas être déplacé hors de l'emprise du parcours
    keepMapInBounds() {
        const target = this.mapControls.target
        this.clampedTarget.copy(target).clamp(this.mapBounds.min, this.mapBounds.max)
        if (this.clampedTarget.equals(target)) return

        this.orthographic.position.add(this.clampedTarget.sub(target))
        target.add(this.clampedTarget)
    }

    /* ---------------- RESIZE ---------------- */

    resize() {
        this.perspective.aspect = this.sizes.width / this.sizes.height
        this.perspective.updateProjectionMatrix()
        if (this.controlMode === 'map') this.updateMapFrame()
    }

    /* ---------------- DESTROY ---------------- */

    destroy() {
        this.controls?.dispose?.()
        this.mapControls?.dispose?.()

        if (this.debug?.active && this.debugFolder) {
            this.debugFolder.dispose()
            this.debugFolder = null
        }

        this.scene.remove(this.perspective, this.orthographic)

        this.perspective = null
        this.orthographic = null
        this.controls = null
        this.mapControls = null
        this.experience = null
    }
}